'use client'

import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react'
import { useSession } from 'next-auth/react'

export type ColorTheme = 'default' | 'blue' | 'pink' | 'yellow' | 'green'

interface AppState {
  theme: 'light' | 'dark'
  toggleTheme: () => void
  colorTheme: ColorTheme
  setColorTheme: (t: ColorTheme) => void
  sidebarCollapsed: boolean
  toggleSidebar: () => void
  mobileMenuOpen: boolean
  setMobileMenu: (open: boolean) => void
}

const AppContext = createContext<AppState | null>(null)

export function useApp() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp must be inside AppProvider')
  return ctx
}

export function AppProvider({ children }: { children: ReactNode }) {
  const { status } = useSession()
  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const [colorTheme, setColorThemeState] = useState<ColorTheme>('default')
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenu] = useState(false)
  const [loaded, setLoaded] = useState(false)

  // Track if we're saving to avoid poll overwriting our own change
  const savingRef = useRef(false)

  // Apply theme classes to DOM
  const applyTheme = useCallback((t: 'light' | 'dark') => {
    document.documentElement.classList.toggle('dark', t === 'dark')
  }, [])

  const applyColorTheme = useCallback((ct: ColorTheme) => {
    const cl = document.documentElement.classList
    ;['theme-blue', 'theme-pink', 'theme-yellow', 'theme-green'].forEach(c => cl.remove(c))
    if (ct !== 'default') cl.add(`theme-${ct}`)
  }, [])

  // Load from localStorage first (instant), then override from API
  useEffect(() => {
    const savedTheme = localStorage.getItem('elizium-theme') as 'light' | 'dark' | null
    const savedColor = localStorage.getItem('elizium-color-theme') as ColorTheme | null
    const savedSidebar = localStorage.getItem('elizium-sidebar')
    if (savedTheme) { setTheme(savedTheme); applyTheme(savedTheme) }
    if (savedColor) { setColorThemeState(savedColor); applyColorTheme(savedColor) }
    if (savedSidebar === 'collapsed') setSidebarCollapsed(true)
    setLoaded(true)
  }, [applyTheme, applyColorTheme])

  // Load from API once authenticated
  useEffect(() => {
    if (status !== 'authenticated') return
    fetch('/api/user/preferences')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return
        const t = data.darkMode ? 'dark' : 'light'
        const ct = (data.colorTheme || 'default') as ColorTheme
        setTheme(t); applyTheme(t); localStorage.setItem('elizium-theme', t)
        setColorThemeState(ct); applyColorTheme(ct); localStorage.setItem('elizium-color-theme', ct)
      })
      .catch(() => {})
  }, [status, applyTheme, applyColorTheme])

  // Poll every 5s for cross-device sync
  useEffect(() => {
    if (status !== 'authenticated') return
    const interval = setInterval(() => {
      if (savingRef.current) return // skip while saving
      fetch('/api/user/preferences')
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data) return
          const t = data.darkMode ? 'dark' : 'light'
          const ct = (data.colorTheme || 'default') as ColorTheme
          setTheme(prev => {
            if (prev !== t) { applyTheme(t); localStorage.setItem('elizium-theme', t) }
            return t
          })
          setColorThemeState(prev => {
            if (prev !== ct) { applyColorTheme(ct); localStorage.setItem('elizium-color-theme', ct) }
            return ct
          })
        })
        .catch(() => {})
    }, 5000)
    return () => clearInterval(interval)
  }, [status, applyTheme, applyColorTheme])

  // Save to API helper
  const saveToAPI = useCallback((data: { colorTheme?: string; darkMode?: boolean }) => {
    savingRef.current = true
    fetch('/api/user/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).finally(() => {
      setTimeout(() => { savingRef.current = false }, 1000)
    })
  }, [])

  // Apply on changes
  useEffect(() => {
    if (!loaded) return
    applyTheme(theme)
    localStorage.setItem('elizium-theme', theme)
  }, [theme, loaded, applyTheme])

  useEffect(() => {
    if (!loaded) return
    applyColorTheme(colorTheme)
    localStorage.setItem('elizium-color-theme', colorTheme)
  }, [colorTheme, loaded, applyColorTheme])

  const toggleTheme = () => {
    setTheme(t => {
      const next = t === 'light' ? 'dark' : 'light'
      saveToAPI({ darkMode: next === 'dark' })
      return next
    })
  }

  const setColorTheme = (t: ColorTheme) => {
    setColorThemeState(t)
    saveToAPI({ colorTheme: t })
  }

  const toggleSidebar = () => {
    setSidebarCollapsed(c => {
      localStorage.setItem('elizium-sidebar', !c ? 'collapsed' : 'expanded')
      return !c
    })
  }

  return (
    <AppContext.Provider value={{ theme, toggleTheme, colorTheme, setColorTheme, sidebarCollapsed, toggleSidebar, mobileMenuOpen, setMobileMenu }}>
      {children}
    </AppContext.Provider>
  )
}
